import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitLead } from "@/lib/api";
import { trackContactConversion } from "@/lib/tracking";
import { Link } from "react-router-dom";

const INTERESTS = [
    { value: "buying-advice", label: "Which printer should I buy?" },
    { value: "business-bulk", label: "Business / bulk purchase" },
    { value: "3d-printing", label: "3D printer help" },
    { value: "troubleshooting", label: "Troubleshooting an existing printer" },
    { value: "editorial", label: "Editorial / press" },
    { value: "other", label: "Something else" },
];

export default function LeadForm({ source = "contact-page", compact = false }) {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", interest: "", message: "",
        consent: false, website: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const set = (k) => (e) => {
        const v = e && e.target ? e.target.value : e;
        setForm(prev => ({ ...prev, [k]: v }));
        setErrors(prev => ({ ...prev, [k]: undefined }));
    };

    const validate = () => {
        const errs = {};
        if (form.name.trim().length < 2) errs.name = "Please enter your name.";
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = "Please enter a valid email.";
        if (form.message.trim().length < 5) errs.message = "Please add a short message.";
        if (!form.consent) errs.consent = "You must accept the privacy policy.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await submitLead({
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                interest: form.interest || null,
                message: form.message.trim(),
                consent: form.consent,
                source,
                website: form.website, // honeypot
            });
            setSubmitted(true);
            trackContactConversion({ event_category: "lead", event_label: source });
            toast.success("Thanks — we'll be in touch within one business day.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="card-flat p-8 md:p-10 text-center" data-testid="lead-form-success">
                <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
                <h3 className="font-display text-2xl font-bold text-white mt-4">Message received</h3>
                <p className="text-zinc-400 mt-3 max-w-md mx-auto leading-relaxed">
                    Thanks for reaching out. A member of our team will reply within one business day.
                    For urgent questions, call us at{" "}
                    <a href="tel:+18779496786" className="text-blue-400 hover:text-blue-300">(877) 949-6786</a>.
                </p>
                <button
                    onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", interest: "", message: "", consent: false, website: "" }); }}
                    data-testid="lead-form-reset"
                    className="btn-ghost mt-6"
                >
                    Send another message
                </button>
            </div>
        );
    }

    const gridCols = compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2";

    return (
        <form onSubmit={onSubmit} data-testid="lead-form" className="card-flat p-6 md:p-8 space-y-5" noValidate>
            {/* Honeypot */}
            <div className="hidden" aria-hidden="true">
                <label>Website</label>
                <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm(prev => ({ ...prev, website: e.target.value }))}
                />
            </div>

            <div className={`grid ${gridCols} gap-5`}>
                <div>
                    <Label htmlFor="lead-name" className="text-xs tracking-[0.18em] uppercase text-zinc-400">Name *</Label>
                    <Input
                        id="lead-name"
                        data-testid="lead-name"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Jane Doe"
                        className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11 focus-visible:ring-blue-500"
                        aria-invalid={!!errors.name}
                    />
                    {errors.name && <div className="text-xs text-red-400 mt-1.5">{errors.name}</div>}
                </div>
                <div>
                    <Label htmlFor="lead-email" className="text-xs tracking-[0.18em] uppercase text-zinc-400">Email *</Label>
                    <Input
                        id="lead-email"
                        data-testid="lead-email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@company.com"
                        className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11 focus-visible:ring-blue-500"
                        aria-invalid={!!errors.email}
                    />
                    {errors.email && <div className="text-xs text-red-400 mt-1.5">{errors.email}</div>}
                </div>
            </div>

            <div className={`grid ${gridCols} gap-5`}>
                <div>
                    <Label htmlFor="lead-phone" className="text-xs tracking-[0.18em] uppercase text-zinc-400">Phone (optional)</Label>
                    <Input
                        id="lead-phone"
                        data-testid="lead-phone"
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="(555) 123-4567"
                        className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11 focus-visible:ring-blue-500"
                    />
                </div>
                <div>
                    <Label className="text-xs tracking-[0.18em] uppercase text-zinc-400">I need help with</Label>
                    <Select value={form.interest} onValueChange={set("interest")}>
                        <SelectTrigger data-testid="lead-interest" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11 focus:ring-blue-500">
                            <SelectValue placeholder="Select a topic (optional)" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-800 rounded-sm">
                            {INTERESTS.map(o => (
                                <SelectItem key={o.value} value={o.value} className="focus:bg-zinc-900 focus:text-white">
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div>
                <Label htmlFor="lead-message" className="text-xs tracking-[0.18em] uppercase text-zinc-400">How can we help? *</Label>
                <Textarea
                    id="lead-message"
                    data-testid="lead-message"
                    value={form.message}
                    onChange={set("message")}
                    rows={5}
                    placeholder="Tell us about your workflow, budget, and what you're trying to print…"
                    className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-blue-500"
                    aria-invalid={!!errors.message}
                />
                {errors.message && <div className="text-xs text-red-400 mt-1.5">{errors.message}</div>}
            </div>

            <label className="flex items-start gap-3 text-sm text-zinc-400 cursor-pointer">
                <Checkbox
                    checked={form.consent}
                    onCheckedChange={(v) => set("consent")(!!v)}
                    data-testid="lead-consent"
                    className="border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 rounded-sm mt-0.5"
                />
                <span>
                    I agree to the <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline underline-offset-4">privacy policy</Link> and consent to
                    All About Printers contacting me about my request. No spam — we never share your info.
                </span>
            </label>
            {errors.consent && <div className="text-xs text-red-400 -mt-3">{errors.consent}</div>}

            <div className="pt-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-zinc-900">
                <div className="text-xs text-zinc-500 leading-relaxed">
                    We typically reply within 1 business day. Prefer to call?{" "}
                    <a href="tel:+18779496786" className="text-blue-400 hover:text-blue-300">(877) 949-6786</a>
                </div>
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-submit"
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send message"}
                </button>
            </div>
        </form>
    );
}
